import { FC, useState, useContext, useCallback, useEffect } from 'react';
import { LeadImage } from '../assets/LeadImage';
import { ArrowRight } from '../assets/ArrowRight';
import { ErrorIcon } from '../assets/ErrorIcon';
import { useNavigate } from "react-router-dom";
import { Loader } from '../components/Loader';
import { AppContext } from '../App';
import { CHECK_ACCOUNT_URL } from '../types/API_URLs';

interface LandingPageProps {
	brand: string;
	contactUsLink: string;
}

export const LandingPage: FC<LandingPageProps> = ({ brand, contactUsLink }) => {
	const navigate = useNavigate();
	const [isAccountFound, setIsAccountFound] = useState(true);
	const [invalidDateOfBirth, setInvalidDateOfBirth] = useState('');
	const [invalidAccountNo, setInvalidAccountNo] = useState('');
	const [invalidLastName, setInvalidLastName] = useState('');
	const [invalidPostCode, setInvalidPostCode] = useState('');
	const { accountNumberReq, setAccountNumberReq } = useContext(AppContext);
	const [uploadState, setUploadState] = useState('none');

	useEffect(() => {
		const loadScriptByURL = (id: string, url: string, callback: () => void) => {
			const isScriptExist = document.getElementById(id);

			if (!isScriptExist) {
				var script = document.createElement("script");
				script.type = "text/javascript";
				script.src = url;
				script.id = id;
				script.onload = function () {
					if (callback) callback();
				};
				document.body.appendChild(script);
			}

			if (isScriptExist && callback) callback();
		}


		loadScriptByURL("recaptcha-key", `https://www.google.com/recaptcha/enterprise.js?render=${process.env.REACT_APP_SITE_KEY}`, function () {

		});
	}, []);

	const getAccountNumber = useCallback((token: string) => {
		setUploadState('loading');
		setAccountNumberReq({ ...accountNumberReq, token: token });
		fetch(CHECK_ACCOUNT_URL, {
			method: 'POST',
			mode: 'cors',
			cache: 'no-cache',
			credentials: 'same-origin',
			headers: { 'Content-Type': 'application/json' },
			redirect: 'follow',
			referrerPolicy: 'no-referrer',
			body: JSON.stringify({ ...accountNumberReq, token: token }),
		}).then(resp => resp.json()).then((resp) => {

			if (resp && typeof resp === 'string') {
				token && token.length > 0 && setAccountNumberReq({ ...accountNumberReq, uuid: resp, token: token });
				!token && setAccountNumberReq({ ...accountNumberReq, uuid: resp });
				navigate('/file-type', {
					state: {
						uuid: resp,
						lastName: accountNumberReq.lastName,
						postcode: accountNumberReq.postcode,
						dateOfBirth: accountNumberReq.dateOfBirth,
						token: token
					}
				});
			} else {
				setIsAccountFound(false);
				setUploadState('error');
			}
		}
		).catch((err) => {
			setUploadState('error');
			setIsAccountFound(false);
		}
		);

	}, [accountNumberReq, setUploadState, setAccountNumberReq, navigate]);


	const handleSubmit = () => {
		if (
			(!accountNumberReq.postcode &&
				!accountNumberReq.lastName &&
				!isAccountNoValid(accountNumberReq.accountNumber)) ||
			(!isDateOfBirthValid(accountNumberReq.dateOfBirth)) ||
			(accountNumberReq.postcode && !isPostCodeValid(accountNumberReq.postcode)) ||
			(accountNumberReq.lastName && !isLastNameValid(accountNumberReq.lastName))
		) {
			return;
		}
		setInvalidAccountNo('');
		if (window.grecaptcha && window.grecaptcha.enterprise) {
			window.grecaptcha.enterprise.ready(() => {
				window.grecaptcha.enterprise.execute(process.env.REACT_APP_SITE_KEY as string, { action: 'submit' }).then(token => {
					if (token && token.length > 1) {
						getAccountNumber(token);
					}
				});
			});
		}
	}

	const isDateOfBirthValid = (dateOfBirth: string): boolean => {
		if (!dateOfBirth || !dateOfBirth.match(/^(0?[1-9]|[12][0-9]|3[01])[\/](0?[1-9]|1[012])[\/]\d{4}$/)) {
			setInvalidDateOfBirth('Invalid date of birth, should be in format: dd/mm/yyyy');
			return false;
		}

		setInvalidDateOfBirth('');
		return true;
	}

	const isLastNameValid = (lastname: string): boolean => {
		if (!lastname || !lastname.match(/^[A-Za-zÀ-ú !,.'-’—]+$/)) {
			setInvalidLastName('Invalid lastname');
			return false;
		}

		setInvalidLastName('');
		return true;

	}

	const isPostCodeValid = (postCode: string) => {
		if (!postCode || !postCode.match(/^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/)) {
			setInvalidPostCode('Invalid postcode');
			return false;
		}

		setInvalidPostCode('');
		return true;

	}

	const isAccountNoValid = (accountNo: string): boolean => {
		if (!accountNo || !accountNo.match(/^[0-9]{3,8}$/g)) {
			setInvalidAccountNo('Invalid customer number, should be 8 digits number');
			return false;
		}
		setInvalidAccountNo('');
		return true;

	}
	//setAccountNumberReq({ ...accountNumberReq, dateOfBirth: '' });
	return (

		< div className="page" >

			<div className="container">

				<LeadImage className='' />
				{uploadState === 'loading' && <Loading />}
				<h1 className="page__title u-m__b--l">Welcome to the Digital Postbox</h1>

				<div className="form u-m__b--l" >
					<legend className="form__legend">Welcome to your new Digital Postbox! Need to send us communication from your lenders, proof of income or something else? No problem you can upload it here! Please enter your details to begin.</legend>
					<fieldset className="u-m__t--l u-m__b--l">
						<label htmlFor="dob" className="form__label">Date of Birth*</label>
						<input id="dob" className="form__input u-m__t" type="text" placeholder="dd/mm/yyyy" onChange={(value) =>
							setAccountNumberReq({ ...accountNumberReq, dateOfBirth: value.currentTarget.value })
						} />
						<span style={{ color: "red" }}>{invalidDateOfBirth}</span>
					</fieldset>
					<fieldset className="u-m__t--l u-m__b--l">
						<label htmlFor="clientId" className="form__label">Your Customer Number</label>
						<input id="clientId" className="form__input u-m__t u-m__b--s" type="text" onChange={(value) => {
							setAccountNumberReq({ ...accountNumberReq, accountNumber: value.currentTarget.value });
						}
						} />
						<span style={{ color: "red" }}>{invalidAccountNo}</span>
						<p className="form__hint">This number can be found on any letters or emails sent to you by Financial Wellness Group.</p>
					</fieldset>
					<fieldset className="u-m__t--l u-m__b--l">
						<p className="form__hint">If you don't know your customer number, please provide your Last Name and Postcode.</p>
					</fieldset>

					<fieldset className="u-m__b--l">
						<label htmlFor="surname" className="form__label">Last Name</label>
						<input id="surname" className="form__input u-m__t" type="text" onChange={(value) =>
							setAccountNumberReq({ ...accountNumberReq, lastName: value.currentTarget.value })
						} />
						<span style={{ color: "red" }}>{invalidLastName}</span>
					</fieldset>

					<fieldset className="u-m__b--l">
						<label htmlFor="postcode" className="form__label">Postcode</label>
						<input id="postcode" className="form__input u-m__t" type="text" onChange={(value) => setAccountNumberReq({ ...accountNumberReq, postcode: value.currentTarget.value })
						} />
						<span style={{ color: "red" }}>{invalidPostCode}</span>
					</fieldset>


					{!isAccountFound && (
						<div className="verification-error u-m__b--l">
							<div className="verification-error__left">
								<ErrorIcon />
							</div>
							<div>
								<p className="verification-error__error">We couldn’t find a customer with these details.</p>
								<p>Please check the information above is correct and try again.</p>
							</div>

						</div>)}

					<input className="btn" type="button" value="Continue" onClick={() => { uploadState !== 'loading' && handleSubmit(); }} />

				</div>

				<div className="u-text-center">
					<p className="u-m__b--s">Having trouble remembering your details?</p>
					<a href={contactUsLink} target="_blank" rel="noreferrer">Contact us <ArrowRight /></a>
				</div>

			</div>
		</div >
	);
}

const Loading: FC = () => {
	return (
		<div>
			<Loader className="loading-anchor" />
			<p>Finding account</p>
		</div>
	);
}